import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

import { GoogleAuthService } from './google-auth.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  // Observable subjects for user state management
  public loggedIn: ReplaySubject<string> = new ReplaySubject();
  public loggedOut: ReplaySubject<string> = new ReplaySubject();

  // User profile information (protected with getters)
  private _service: string = '';
  get service(): string { return this._service; }

  private _id: string = '';
  get id(): string { return this._id; }

  private _email: string = '';
  get email(): string { return this._email; }

  private _domain: string = '';
  get domain(): string { return this._domain; }

  private _displayName: string = '';
  get displayName(): string { return this._displayName; }

  private _lastName: string = '';
  get lastName(): string { return this._lastName; }

  private _firstName: string = '';
  get firstName(): string { return this._firstName; }

  private _imageUrl: string = '';
  get imageUrl(): string { return this._imageUrl; }

  constructor(
    private googleAuth: GoogleAuthService,
    private utils: UtilsService
  ) {
    this.utils.logDebug('user service constructor called');

    // Subscribe to Google authentication events
    this.googleAuth.loggedIn.subscribe(() => {
      this.utils.logDebug('user service received google login update');
      this.handleGoogleLogin();
    });

    this.googleAuth.loggedOut.subscribe(() => {
      this.utils.logDebug('user service received google logout update');
      this.handleLogout('google');
    });

    // Handle logout cleanup
    this.loggedOut.subscribe(() => {
      this.clearUserData();
    });
  }

  // Handle successful Google login
  private handleGoogleLogin(): void {
    this._service = 'google';
    this._id = this.googleAuth.id;
    this._email = this.googleAuth.email;
    this._domain = this.googleAuth.domain;
    this._displayName = this.googleAuth.displayName;
    this._lastName = this.googleAuth.lastName;
    this._firstName = this.googleAuth.firstName;
    this._imageUrl = this.googleAuth.imageUrl;

    // Notify the rest of the application
    this.loggedIn.next('google');
  }

  // Handle user logout
  private handleLogout(service: string): void {
    this.loggedOut.next(service);
  }

  // Clear all user data
  private clearUserData(): void {
    this._service = '';
    this._id = '';
    this._email = '';
    this._domain = '';
    this._displayName = '';
    this._lastName = '';
    this._firstName = '';
    this._imageUrl = '';
  }

  // Public method to initiate logout
  public logout(): void {
    if (this.service === 'google') {
      this.googleAuth.loggedOut.next(true);
    }
  }

  // Check if user is currently logged in
  public isLoggedIn(): boolean {
    return this._service !== '' && this._id !== '' && this._email !== '';
  }

  // Get user's full name
  public getFullName(): string {
    if (this._displayName) {
      return this._displayName;
    } else if (this._firstName && this._lastName) {
      return `${this._firstName} ${this._lastName}`;
    } else if (this._firstName) {
      return this._firstName;
    } else if (this._lastName) {
      return this._lastName;
    }
    return this._email || '';
  }

  // Get user's initials
  public getInitials(): string {
    if (this._firstName && this._lastName) {
      return `${this._firstName.charAt(0)}${this._lastName.charAt(0)}`.toUpperCase();
    } else if (this._displayName) {
      const names = this._displayName.split(' ');
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
      } else if (names.length === 1) {
        return names[0].substring(0, 2).toUpperCase();
      }
    } else if (this._email) {
      return this._email.substring(0, 2).toUpperCase();
    }
    return 'U';
  }

  // Check if user has admin permissions (based on full scopes)
  public hasAdminPermissions(): boolean {
    return this.googleAuth.fullScopes;
  }

  // Get authentication tokens (for API calls)
  public getIdToken(): string {
    return this.googleAuth.idToken;
  }

  public getAccessToken(): string {
    return this.googleAuth.accessToken;
  }

  // Check if user is from Gmail (should be blocked)
  public isGmailUser(): boolean {
    return this.googleAuth.isGmailAddress(this._email);
  }
}